import { getRelationGroups } from "api/relations";
import type { RelationGroupDto } from "api/types";
import { useEffect, useState } from "react";
import { useSession } from "Session";

/**
 * The one request for the whole session. The groups are fixed reference data, so every entity dialog
 * shares a single fetch rather than each one opening its own. A failed request is forgotten, so the
 * next dialog to open tries again instead of inheriting the failure.
 */
let pendingGroups: Promise<RelationGroupDto[]> | null = null;

const loadRelationGroups = (getAccessToken: () => Promise<string>): Promise<RelationGroupDto[]> => {
	if (!pendingGroups) {
		pendingGroups = getAccessToken()
			.then((token: string) => getRelationGroups(token))
			.catch((error: unknown) => {
				pendingGroups = null;
				throw error;
			});
	}

	return pendingGroups;
};

/**
 * Relation groups for the relationship picker, served by the API. Empty while loading and if the
 * request fails — the picker treats that as "no grouping" and offers one flat list, so a relationship
 * can always be added even when the groups never arrive.
 */
export const useRelationGroups = (): RelationGroupDto[] => {
	const { getAccessToken } = useSession();
	const [groups, setGroups] = useState<RelationGroupDto[]>([]);

	useEffect(() => {
		let isCurrent = true;

		loadRelationGroups(getAccessToken)
			.then((loaded: RelationGroupDto[]) => {
				if (isCurrent) {
					setGroups(loaded);
				}
			})
			.catch(() => undefined);

		return () => {
			isCurrent = false;
		};
	}, [getAccessToken]);

	return groups;
};
